import { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface MobileNavItem {
  to: string;
  label: string;
  icon: string;
}

interface MobileNativeShellProps {
  headerTitle?: string;
  headerLeft?: ReactNode;
  headerRight?: ReactNode;
  headerCenter?: ReactNode;
  children: ReactNode;
  bottomNavItems?: MobileNavItem[];
  bottomNav?: ReactNode;
  bottomNavHeight?: number;
  headerHeight?: number;
  className?: string;
  showHeader?: boolean;
  showBottomNav?: boolean;
  onBack?: () => void;
  safeAreaTop?: boolean;
  safeAreaBottom?: boolean;
}

export default function MobileNativeShell({
  headerTitle,
  headerLeft,
  headerRight,
  headerCenter,
  children,
  bottomNavItems,
  bottomNav,
  bottomNavHeight = 64,
  headerHeight = 56,
  className = '',
  showHeader = true,
  showBottomNav = true,
  onBack,
  safeAreaTop = true,
  safeAreaBottom = true,
}: MobileNativeShellProps) {
  const location = useLocation();

  const hasBottomNav = showBottomNav && (bottomNav || (bottomNavItems && bottomNavItems.length > 0));

  return (
    <div
      className={`h-[100dvh] w-full flex flex-col bg-white overflow-hidden ${className}`}
      style={{
        paddingTop: safeAreaTop ? 'env(safe-area-inset-top)' : undefined,
        paddingBottom: safeAreaBottom && hasBottomNav ? 'env(safe-area-inset-bottom)' : undefined,
      }}
    >
      {/* Mobile Header */}
      {showHeader && (
        <header
          className="flex-shrink-0 bg-white border-b border-gray-100 flex items-center px-4 z-30"
          style={{ height: `${headerHeight}px` }}
        >
          {/* Left */}
          <div className="flex items-center flex-shrink-0 w-12">
            {headerLeft ? (
              headerLeft
            ) : onBack ? (
              <button
                onClick={onBack}
                className="w-11 h-11 flex items-center justify-center text-gray-700 cursor-pointer"
                aria-label="Volver"
              >
                <i className="ri-arrow-left-line text-xl"></i>
              </button>
            ) : (
              <div className="w-11 h-11" />
            )}
          </div>

          {/* Center */}
          <div className="flex-1 flex items-center justify-center min-w-0">
            {headerCenter ? (
              headerCenter
            ) : headerTitle ? (
              <h1 className="text-base font-semibold text-gray-900 truncate px-2">
                {headerTitle}
              </h1>
            ) : null}
          </div>

          {/* Right */}
          <div className="flex items-center justify-end flex-shrink-0 w-12">
            {headerRight || <div className="w-11 h-11" />}
          </div>
        </header>
      )}

      {/* Main scrollable content — padding-bottom accounts for bottom nav + safe area */}
      <main
        className="flex-1 overflow-y-auto min-w-0 w-full"
        style={{
          paddingBottom: hasBottomNav
            ? `calc(${bottomNavHeight}px + env(safe-area-inset-bottom, 0px) + 8px)`
            : undefined,
        }}
      >
        {children}
      </main>

      {/* Bottom Navigation */}
      {hasBottomNav && (
        <>
          {bottomNav ? (
            bottomNav
          ) : (
            <nav
              className="flex-shrink-0 bg-white border-t border-gray-100 flex items-center justify-around z-30"
              style={{
                height: `${bottomNavHeight}px`,
                paddingBottom: 'env(safe-area-inset-bottom, 0px)',
              }}
            >
              {bottomNavItems!.map((item) => {
                const isActive =
                  location.pathname === item.to ||
                  location.pathname.startsWith(item.to + '/');
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`flex flex-col items-center justify-center gap-0.5 flex-1 h-full cursor-pointer select-none min-h-[44px] ${
                      isActive ? 'text-primary-500' : 'text-gray-400'
                    }`}
                  >
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className={`${item.icon} text-xl`}></i>
                    </div>
                    <span className="text-[12px] font-medium leading-tight whitespace-nowrap">{item.label}</span>
                  </Link>
                );
              })}
            </nav>
          )}
        </>
      )}
    </div>
  );
}